// __PLANNING__ (2026-10-26) the Planning workspace's rules, sorting and "goes well together" hints. Pure: no React, no
// Supabase, so sim/planning.test.mjs runs it as is. The lists / rules / main lines are kept by the hub (planning.py,
// GET /api/board/planning); a job's places come from the geodata the hub keeps on it (billing_data.geo, job_geo.py),
// else from its stops' own columns.
//
// A job lands in the FIRST enabled list whose rule it matches (the lists' order is the priority); the other lists it
// matches are its "also matches". A card dragged to a list stays there (a placement) until it is dragged back.

export const UNSORTED = '__unsorted__';
const R = 6371;
const rad = (d) => (d * Math.PI) / 180;
const num = (v) => { const f = typeof v === 'number' ? v : parseFloat(v); return Number.isFinite(f) ? f : null; };
const low = (s) => String(s == null ? '' : s).trim().toLowerCase();

export function kmBetween(a, b) {
  if (!a || !b) return null;
  const h = Math.sin(rad(b[0] - a[0]) / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(rad(b[1] - a[1]) / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function bearingOf(a, b) {
  if (!a || !b) return null;
  const y = Math.sin(rad(b[1] - a[1])) * Math.cos(rad(b[0]));
  const x = Math.cos(rad(a[0])) * Math.sin(rad(b[0])) - Math.sin(rad(a[0])) * Math.cos(rad(b[0])) * Math.cos(rad(b[1] - a[1]));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

// Which side of a main line (a west -> east trace) a point is on: 'north' | 'south' | null (beyond its ends).
export function sideOf(pt, line) {
  const p = (line && line.points) || [];
  if (!pt || p.length < 2) return null;
  const s = [...p].sort((u, v) => u[1] - v[1]);
  if (pt[1] < s[0][1] || pt[1] > s[s.length - 1][1]) return null;
  for (let i = 0; i < s.length - 1; i++) {
    const [la1, ln1] = s[i]; const [la2, ln2] = s[i + 1];
    if (pt[1] >= ln1 && pt[1] <= ln2) {
      const t = ln2 === ln1 ? 0 : (pt[1] - ln1) / (ln2 - ln1);
      return pt[0] >= la1 + t * (la2 - la1) ? 'north' : 'south';
    }
  }
  return null;
}

export function inPolygon(pt, poly) {
  if (!pt || !poly || poly.length < 3) return false;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [yi, xi] = poly[i]; const [yj, xj] = poly[j];
    if (((yi > pt[0]) !== (yj > pt[0])) && (pt[1] < ((xj - xi) * (pt[0] - yi)) / ((yj - yi) || 1e-12) + xi)) inside = !inside;
  }
  return inside;
}

// Area tags for a stop that has none (a job whose geodata is not in yet): the hub's own areas (job_geo.AREAS) - within
// the radius, or the town / municipality among `names`, or the county among `counties`.
const wordIn = (w, text) => !!text && new RegExp('(^|[^a-z])' + low(w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '([^a-z]|$)').test(text);
export function areaTags(stop, areas) {
  const out = [];
  const places = [stop.town, stop.municipality].map(low).filter(Boolean).join(' | ');
  const county = low(stop.county);
  (areas || []).forEach((a) => {
    let hit = false;
    if (stop.pt) { const d = kmBetween(stop.pt, [a.lat, a.lng]); hit = d != null && d <= (a.radius_km || 0); }
    if (!hit) hit = (a.names || []).some((n) => wordIn(n, places)) || (a.counties || []).some((n) => wordIn(n, county));
    if (hit && !out.includes(a.name)) out.push(a.name);
  });
  return out;
}

function parseBd(bd) {
  if (!bd) return {};
  if (typeof bd === 'object') return bd;
  try { const o = JSON.parse(bd); return o && typeof o === 'object' ? o : {}; } catch (e) { return {}; }
}

const townOfAddress = (a) => {
  const parts = String(a || '').split(',').map((x) => x.trim()).filter(Boolean);
  return parts.length >= 2 ? parts[1].replace(/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/i, '').replace(/\b(ON|Ontario)\b/i, '').trim() : '';
};

function routeKm(stops) {
  let t = 0; let ok = false;
  for (let i = 0; i < stops.length - 1; i++) { const d = kmBetween(stops[i].pt, stops[i + 1].pt); if (d != null) { t += d * 1.25; ok = true; } }
  return ok ? Math.round(t) : null;
}

// A hauling job (loads row with load_stops) -> a planning item.
export function jobItem(load, areas) {
  const bd = parseBd(load.billing_data);
  const geo = bd.geo && bd.geo.v ? bd.geo : null;
  const raw = [...(load.load_stops || [])].filter((s) => String(s.address || '').trim()).sort((a, b) => (a.stop_sequence || 0) - (b.stop_sequence || 0));
  const gs = geo && Array.isArray(geo.stops) && geo.stops.length === raw.length ? geo.stops : null;
  const stops = raw.map((s, i) => {
    const g = gs ? gs[i] : {};
    const la = num(s.latitude) ?? num(g.lat); const ln = num(s.longitude) ?? num(g.lng);
    const st = {
      address: s.address, name: s.place_name || '', type: s.stop_type || '', pt: la != null && ln != null ? [la, ln] : null,
      town: g.town || s.city || townOfAddress(s.address), municipality: g.municipality || '', county: g.county || '',
      province: g.province || s.province || '', postal: g.postal_code || '', done: !!s.completed,
    };
    st.tags = Array.isArray(g.tags) && gs ? g.tags : areaTags(st, areas);
    return st;
  });
  const r = (geo && geo.route) || {};
  const km = num(r.total_km) ?? num(r.main_km) ?? routeKm(stops);
  return {
    key: 'L:' + load.id, kind: 'job', id: load.id, loadNumber: load.load_number || '', status: load.status || '',
    driverId: load.driver_id || null, startedAt: load.started_at || null,
    customer: (load.customer && load.customer.company_name) || load.customer_name || '',
    date: load.scheduled_date ? String(load.scheduled_date).slice(0, 10) : '', stops, km, stopCount: stops.length,
    driveMin: num(r.drive_min), hasGeo: !!gs,
  };
}

// A Trello-only card from the hub -> a planning item (read-only).
export function trelloItem(card, areas) {
  const stops = (card.stops || []).map((g) => {
    const st = { address: g.address, name: '', type: '', pt: num(g.lat) != null && num(g.lng) != null ? [num(g.lat), num(g.lng)] : null,
      town: g.town || townOfAddress(g.address), municipality: g.municipality || '', county: g.county || '', province: g.province || '', postal: g.postal_code || '' };
    st.tags = Array.isArray(g.tags) && g.tags.length ? g.tags : areaTags(st, areas);
    return st;
  });
  return { key: card.key || ('T:' + card.id), kind: 'trello', id: card.id, name: card.name || '', list: card.list || '', url: card.url || '',
    customer: '', date: card.day ? String(card.day).slice(0, 10) : (card.due ? String(card.due).slice(0, 10) : ''), nextDay: !!card.day,
    stops, km: routeKm(stops), stopCount: stops.length, readOnly: true };
}

// __SPECIAL_CARDS__ (2026-10-28) a job whose Trello card sits below its driver list's Tomorrow divider (the card tagged
// #divider-tomorrow, any title) is on the NEXT BUSINESS DAY the hub gives it (GET /api/board/planning 'next_day':
// {load id: 'YYYY-MM-DD'}), or on its own booked day when that is later; a job already started keeps its day.
export function applyNextDay(items, nextDay) {
  const m = nextDay || {};
  return (items || []).map((it) => {
    const d = it && it.kind === 'job' ? m[it.id] : null;
    if (!d || it.startedAt || !['pending', 'assigned', ''].includes(String(it.status || '').toLowerCase())) return it;
    return d > (it.date || '') ? { ...it, date: d, nextDay: true } : it;
  });
}

function stopsAt(item, at) {
  const s = item.stops || [];
  if (!s.length) return [];
  if (at === 'start') return [s[0]];
  if (at === 'end') return [s[s.length - 1]];
  return s;
}

function stopTest(st, c, ctx) {
  switch (c.type) {
    case 'place': {
      const vals = (c.values || []).map(low).filter(Boolean);
      if (c.field === 'area') return (st.tags || []).some((t) => vals.includes(low(t)));
      if (c.field === 'postal') return vals.some((v) => low(st.postal).replace(/\s/g, '').startsWith(v.replace(/\s/g, '')));
      return vals.includes(low(st[c.field]));
    }
    case 'radius': { const d = kmBetween(st.pt, [num(c.lat), num(c.lng)]); return d != null && d <= num(c.km); }
    case 'polygon': return inPolygon(st.pt, c.points);
    case 'side': { const line = (ctx.lines || []).find((l) => String(l.id) === String(c.line)); return !!line && sideOf(st.pt, line) === c.side; }
    default: return false;
  }
}

const inRange = (v, mn, mx) => v != null && (mn == null || v >= mn) && (mx == null || v <= mx);

export function conditionMatch(item, c, ctx) {
  let ok;
  if (['place', 'radius', 'polygon', 'side'].includes(c.type)) {
    const ss = stopsAt(item, c.at);
    ok = ss.length > 0 && (c.at === 'all' ? ss.every((st) => stopTest(st, c, ctx)) : ss.some((st) => stopTest(st, c, ctx)));
  } else if (c.type === 'bearing') {
    const s = item.stops || [];
    const b = s.length >= 2 ? bearingOf(s[0].pt, s[s.length - 1].pt) : null;
    const mn = num(c.min) ?? 0; const mx = num(c.max) ?? 360;
    ok = b != null && (mn <= mx ? b >= mn && b <= mx : b >= mn || b <= mx);
  } else if (c.type === 'distance') ok = inRange(num(item.km), num(c.min), num(c.max));
  else if (c.type === 'stops') ok = inRange(item.stopCount, num(c.min), num(c.max));
  else ok = false;
  return c.not ? !ok : ok;
}

export function listMatch(item, list, ctx) {
  const cs = list.conditions || [];
  if (!cs.length) return false;   // a list with no rule is filled by hand only
  return list.match === 'any' ? cs.some((c) => conditionMatch(item, c, ctx)) : cs.every((c) => conditionMatch(item, c, ctx));
}

// -> { first: list id | null, also: [list ids] }
export function classify(item, settings) {
  const ctx = { lines: (settings && settings.lines) || [] };
  const hits = ((settings && settings.lists) || []).filter((l) => l.enabled !== false && listMatch(item, l, ctx)).map((l) => l.id);
  return { first: hits[0] || null, also: hits.slice(1) };
}

const byDate = (a, b) => (a.date || '9999').localeCompare(b.date || '9999') || low(a.customer || a.name).localeCompare(low(b.customer || b.name));

// items -> { columns: [{ list, cards: [{ item, match, pinned, pos }] }] } in the lists' order, then "Not sorted".
export function arrange(items, settings, placements) {
  const lists = ((settings && settings.lists) || []).filter((l) => l.enabled !== false);
  const ids = new Set(lists.map((l) => l.id));
  const cols = {}; lists.forEach((l) => { cols[l.id] = []; }); cols[UNSORTED] = [];
  items.forEach((item) => {
    const match = classify(item, settings);
    const p = placements && placements[item.key];
    const pinned = !!(p && p.list && (ids.has(p.list) || p.list === UNSORTED) && !item.readOnly);
    const col = pinned ? p.list : (match.first || UNSORTED);
    cols[col].push({ item, match, pinned, pinPos: pinned ? num(p.pos) : null });
  });
  const out = [...lists.map((l) => ({ list: l, cards: cols[l.id] })), { list: { id: UNSORTED, name: 'Not sorted', color: '#8b949e', enabled: true, conditions: [] }, cards: cols[UNSORTED] }];
  out.forEach((c) => {
    const free = c.cards.filter((x) => x.pinPos == null).sort((a, b) => byDate(a.item, b.item));
    free.forEach((x, i) => { x.pos = 1000 + i * 10; });
    c.cards.filter((x) => x.pinPos != null).forEach((x) => { x.pos = x.pinPos; });
    c.cards.sort((a, b) => a.pos - b.pos || byDate(a.item, b.item));
  });
  return { columns: out };
}

// The position a card dropped at `index` of `cards` (the column without the card) gets.
export function dropPos(cards, index) {
  const prev = cards[index - 1]; const next = cards[index];
  if (!prev && !next) return 1000;
  if (!prev) return next.pos - 10;
  if (!next) return prev.pos + 10;
  return (prev.pos + next.pos) / 2;
}

const daysApart = (a, b) => (a && b ? Math.abs((Date.parse(a) - Date.parse(b)) / 86400000) : 0);

// "Goes well together": { key: [{ key, kind: 'backhaul' | 'return' | 'same lane', km }] }
//   backhaul  - the other job picks up near where this one drops off (this one first)
//   return    - this one picks up near where the other drops off (the other first)
//   same lane - both pick up near each other and drop off near each other
export function combos(items, opt) {
  const near = num(opt && opt.near_km) ?? 25; const days = num(opt && opt.days) ?? 1;
  const out = {};
  const ends = items.map((it) => { const s = it.stops || []; return s.length >= 2 && s[0].pt && s[s.length - 1].pt ? [s[0].pt, s[s.length - 1].pt] : null; });
  const add = (k, x) => { (out[k] = out[k] || []).push(x); };
  for (let i = 0; i < items.length; i++) {
    if (!ends[i]) continue;
    for (let j = i + 1; j < items.length; j++) {
      if (!ends[j] || daysApart(items[i].date, items[j].date) > days) continue;
      const [si, ei] = ends[i]; const [sj, ej] = ends[j];
      const ss = kmBetween(si, sj); const ee = kmBetween(ei, ej);
      if (ss <= near && ee <= near) {
        add(items[i].key, { key: items[j].key, kind: 'same lane', km: Math.round(Math.max(ss, ee)) });
        add(items[j].key, { key: items[i].key, kind: 'same lane', km: Math.round(Math.max(ss, ee)) });
        continue;
      }
      const ij = kmBetween(ei, sj); const ji = kmBetween(ej, si);
      if (ij <= near) { add(items[i].key, { key: items[j].key, kind: 'backhaul', km: Math.round(ij) }); add(items[j].key, { key: items[i].key, kind: 'return', km: Math.round(ij) }); }
      if (ji <= near) { add(items[j].key, { key: items[i].key, kind: 'backhaul', km: Math.round(ji) }); add(items[i].key, { key: items[j].key, kind: 'return', km: Math.round(ji) }); }
    }
  }
  Object.values(out).forEach((l) => l.sort((a, b) => a.km - b.km));
  return out;
}

// The card's face: route by town (business names on hover), the date, km and stops.
export function face(item) {
  const s = item.stops || [];
  const t = (x) => (x && (x.town || x.municipality || String(x.address || '').split(',')[0])) || '?';
  const route = s.length === 0 ? (item.name || '') : s.length === 1 ? t(s[0]) : t(s[0]) + ' → ' + (s.length > 2 ? '… +' + (s.length - 2) + ' → ' : '') + t(s[s.length - 1]);
  const tags = []; s.forEach((x) => (x.tags || []).forEach((g) => { if (!tags.includes(g)) tags.push(g); }));
  const tip = [item.kind === 'job' && item.loadNumber ? 'Load ' + item.loadNumber : (item.kind === 'trello' ? 'Trello: ' + item.name : '')]
    .concat(item.nextDay ? ['Below the Tomorrow divider in Trello: next business day'] : [])
    .concat(s.map((x, i) => (i + 1) + '. ' + (x.name ? x.name + ' - ' : '') + x.address)).filter(Boolean).join('\n');
  return { title: item.customer || (item.kind === 'trello' ? 'Trello card' : 'Job'), route, tags, tip,
    meta: [item.date, item.km != null ? Math.round(item.km) + ' km' : '', s.length > 2 ? s.length + ' stops' : ''].filter(Boolean).join(' · ') };
}

// Search and the date filter.
export function visible(item, f) {
  if (f && f.q) {
    const q = low(f.q);
    const hay = low([item.customer, item.name, item.loadNumber, ...(item.stops || []).flatMap((s) => [s.address, s.name, s.town])].join(' '));
    if (!hay.includes(q)) return false;
  }
  if (f && f.date && item.date && item.date !== f.date) return false;
  if (f && f.date && !item.date && !f.undated) return false;
  if (f && f.hideTrello && item.kind === 'trello') return false;
  return true;
}

// ---------------------------------------------------------------- __PLANNING_ASSIGN__ (P2 / P3)
// Assigning from a card, and what a REassignment means (P3). Re-read the job LIVE before acting (a card can be stale).
export const CLOSED_STATUSES = ['complete', 'completed', 'ready_to_bill', 'billed', 'archived', 'cancelled'];
export const EN_ROUTE_STATUSES = ['en_route', 'at_pickup'];
export const PICKED_UP_STATUSES = ['loading', 'in_transit', 'at_delivery', 'unloading', 'pending_completion'];

// -> { tier, text }
//   ok         nobody has it
//   assigned   a driver has it, not started: their route must be reconfirmed in Route Builder
//   en_route   the driver is on the way to it: they are told, and the route reconfirmed
//   picked_up  the load is on their truck (a pickup is done, or it is loading / in transit / delivering): it stays
//              with them - only a dispatch override with a reason moves it
//   blocked    finished / billed / cancelled
export function reassignCheck(load) {
  const s = low(load && load.status);
  if (CLOSED_STATUSES.includes(s)) return { tier: 'blocked', text: 'This job is ' + (s || 'finished') + ' - it cannot be assigned.' };
  if (!load || !load.driver_id) return { tier: 'ok', text: '' };
  const picked = PICKED_UP_STATUSES.includes(s) || (load.load_stops || []).some((x) => x.completed && low(x.stop_type) !== 'delivery');
  if (picked) return { tier: 'picked_up', text: 'The load is already picked up - it is on the driver\'s truck, so it stays with them. Only a dispatch override moves it, with a reason (recorded).' };
  if (EN_ROUTE_STATUSES.includes(s) || load.started_at) return { tier: 'en_route', text: 'The driver is on the way to it. Reassigning takes it off them: they are told, and their route must be reconfirmed in Route Builder.' };
  return { tier: 'assigned', text: 'It is already assigned. Reassigning takes it off their route, which then has to be reconfirmed in Route Builder.' };
}

// Driver lists (Trello-style): "Unassigned" first, then one list per driver, A-Z; each with the jobs that driver has.
// __JOB_ORDER__ order: the hub's job order doc (services/hubPlanning getJobOrder): each driver's list in his order (the
// same as his Trello list and the Driver app), the jobs not ordered yet after them by day.
export function driverColumns(items, drivers, order) {
  const jobs = items.filter((i) => i.kind === 'job');
  const ids = new Set((drivers || []).map((d) => d.id));
  const cols = [{ list: { id: 'drv:none', name: 'Unassigned', color: '#8b949e' }, driverId: null, cards: [] }];
  [...(drivers || [])].sort((a, b) => low(a.name).localeCompare(low(b.name))).forEach((d) => {
    cols.push({ list: { id: 'drv:' + d.id, name: d.name || 'Driver', color: d.is_available === false ? '#6e7681' : '#3fb950' }, driverId: d.id, cards: [] });
  });
  const by = {}; cols.forEach((c) => { by[c.driverId || ''] = c; });
  jobs.forEach((item) => {
    const c = item.driverId && ids.has(item.driverId) ? by[item.driverId] : by[''];
    c.cards.push({ item, match: { first: null, also: [] }, pinned: false, pos: 0 });
  });
  cols.forEach((c) => {
    const ids = c.driverId && order && order.drivers && order.drivers[c.driverId] ? (order.drivers[c.driverId].ids || []) : [];
    const m = new Map(); ids.forEach((id, i) => { if (!m.has(String(id))) m.set(String(id), i); });   // (twin of utils/jobOrder.js indexMap)
    const rk = (x) => (m && m.has(String(x.item.id)) ? m.get(String(x.item.id)) : Infinity);
    c.cards.sort((a, b) => { const ra = rk(a); const rb = rk(b); if (ra !== rb) return ra === Infinity ? 1 : (rb === Infinity ? -1 : ra - rb); return byDate(a.item, b.item); });
    c.cards.forEach((x, i) => { x.pos = 1000 + i * 10; });
  });
  return cols;
}
